import { createSelector } from 'reselect';
import { formatMoney, getUserImageSrc, sizeSort } from '../utils';
import {
  POD_BASE_SIZE_GROUP_LABEL,
  POD_BASE_SIZE_NAMES,
  POD_ONE_SIZE_LABEL,
  POD_SURCHARGE_SIZE_NAMES,
  isPoDSource,
  normalizePodSizeLabel,
  normalizePodSizeName,
} from '../pod';

const TBD_FILE_ID = '00000000-0000-0000-0000-000000000000';

/** Prefer DST embroidery preview PNG when present; otherwise the artwork file. */
export const getArtworkDisplayImage = (artwork, files = {}) => {
  const previewFileId = artwork?.artwork_dst?.preview_file_id;
  if (previewFileId) {
    return files[previewFileId] || { file_id: previewFileId, s3: 1 };
  }
  if (artwork?.artwork_file_id && artwork.artwork_file_id !== TBD_FILE_ID) {
    return files[artwork.artwork_file_id] || null;
  }
  return null;
};

const getItems = state => state.entities.items;
const getItemId = (state, ownProps) => ownProps.item_id;
const getProducts = state => state.entities.products;
const getProductId = (state, ownProps) => ownProps.product_id;
const getDivisions = state => state.entities.divisions;
const getDivisionId = (state, ownProps) => ownProps.division_id;
const getWarnings = state => state.entities.item_warnings;
const getBreakdowns = state => state.entities.breakdowns;
const getCosts = state => state.entities.item_costs;
const getAdjustments = state => state.entities.item_retail_adjustments;
const getItemCostId = (state, ownProps) => ownProps.item_cost_id;
const getComments = state => state.entities.comments;
const getUsers = state => state.entities.users;
const getLocations = state => state.entities.item_locations;
const getArtworks = state => state.entities.artworks;
const getLocationId = (state, ownProps) => ownProps.item_location_id;
const getArtworkId = (state, ownProps) => ownProps.artwork_id;
const getFiles = state => state.entities.files;
const getOrders = state => state.entities.orders;
const getImages = state => state.entities.item_images;
const getOrderType = (state, ownProps) => ownProps.order_type;
const getOrderNumber = (state, ownProps) => +ownProps.form_number;
const getSkus = state => state.entities.skus;
const getDecorations = state => state.entities.item_decorations;
const getDecorationId = (state, ownProps) => ownProps.item_decoration_id;
const getInventoryItems = state => state.entities.inventory_items;
const getColors = state => state.entities.colors;
const getSizes = state => state.entities.sizes;
const EMPTY_PRODUCT_SIZES = {};
const getProductSizes = state => state.dropdowns?.product_sizes ?? EMPTY_PRODUCT_SIZES;

const createSort = field_name => (a, b) => {
  if (a[field_name] < b[field_name]) {
    return -1;
  } else if (a[field_name] > b[field_name]) {
    return 1;
  } else {
    return 0;
  }
};

const normalizedOneSizeLabel = normalizePodSizeName(POD_ONE_SIZE_LABEL);
const normalizedBaseSizeGroupLabel = normalizePodSizeName(POD_BASE_SIZE_GROUP_LABEL);

const isObjectLike = value => null != value && 'object' === typeof value;

const getPodAvailableSizeNames = (item, sizes, product_sizes) => {
  const explicitSizes = (item.item_sizes || []).map(size => size?.size_name);
  const embeddedProductSizes = isObjectLike(item.product_sizes)
    ? Object.values(item.product_sizes).map(size => size?.size_name)
    : [];
  const productSizes = (product_sizes[item.parent_id] || []).map((size_id) => {
    const size = sizes[size_id];
    return size ? size.size_name : null;
  });

  return new Set(
    explicitSizes
      .concat(embeddedProductSizes)
      .concat(productSizes)
      .map(normalizePodSizeName)
      .filter(Boolean),
  );
};

const getPoDOptionSizeLabel = (sizeName) => {
  const normalizedSizeName = normalizePodSizeName(sizeName);
  if (normalizedSizeName === normalizedBaseSizeGroupLabel || POD_SURCHARGE_SIZE_NAMES.includes(normalizedSizeName)) {
    return normalizedSizeName;
  }

  return POD_ONE_SIZE_LABEL;
};

const toOneSizeOptions = (options) => {
  if (0 === options.length) {
    return [];
  }

  const optionsWithQuantity = options.filter(option => Number(option.quantity || 0) > 0);
  if (optionsWithQuantity.length > 0) {
    return optionsWithQuantity.map(option => ({
      ...option,
      size_name: getPoDOptionSizeLabel(option.size_name),
    }));
  }

  const [lowestCostOption] = [...options].sort(
    (a, b) => Number(a.unit_cost || 0) - Number(b.unit_cost || 0),
  );

  return [{ ...lowestCostOption, size_name: getPoDOptionSizeLabel(lowestCostOption.size_name) }];
};

export const getItemColors = createSelector(
  getItems,
  getItemId,
  (items, item_id) => items[item_id].item_colors.map(
    ic => ({ id: ic.color_id, name: ic.color_name })
  ).filter(
    c => c.name !== 'TBD'
  ).sort(createSort('name'))
);

export const getItemSizes = createSelector(
  getItems,
  getItemId,
  (items, item_id) => items[item_id].item_sizes.map(
    i => ({ id: i.size_id, name: i.size_name })
  ).filter(
    s => s.name !== 'TBD'
  ).sort((a, b) => sizeSort(a.name, b.name))
);

export const getItemSkus = createSelector(
  getItems,
  getItemId,
  (items, item_id) => items[item_id].item_skus.map(
    i => ({ id: i.product_sku_id, name: i.sku_description, item_sku_id: i.item_sku_id, sku: i.sku, options: i.options })
  ).sort(createSort('name'))
);

export const getItem = createSelector(
  getItems,
  getInventoryItems,
  getItemId,
  (items, inventory_items, item_id) => !!items[item_id] && ({
    ...items[item_id],
    isPS: 'ps-products' === items[item_id].copied_from && !!items[item_id].ext_fob_id,
    isPoD: isPoDSource(items[item_id].copied_from),
    hasInventory: Object.values(inventory_items).filter(ii => ii.source_item_id === item_id).length > 0
  })
);

export const getItemInventoryByItemId = createSelector(
  getInventoryItems,
  getItems,
  getColors,
  getSizes,
  getSkus,
  getItemId,
  (inventory_items, items, colors, sizes, skus, item_id) => Object.values(inventory_items).filter(
    ii => ii.source_item_id === item_id
  ).map(
    ii => ({
      ...ii,
      color_name: (items[item_id].item_colors.filter(c => c.color_id === ii.color_id)[0] ?? colors[ii.color_id])?.color_name,
      size_name: (items[item_id].item_sizes.filter(s => s.size_id === ii.size_id)[0] ?? sizes[ii.size_id])?.size_name,
      sku_description: (items[item_id].item_skus.filter(s => s.product_sku_id === ii.product_sku_id)[0] ?? skus[ii.product_sku_id])?.sku_description ?? (items[item_id].item_skus.filter(s => s.product_sku_id === ii.product_sku_id)[0] ?? skus[ii.product_sku_id])?.sku
   })
  )
);

export const getOptionItems = createSelector(
  getItems,
  getBreakdowns,
  getSizes,
  getProductSizes,
  getItemId,
  (items, breakdowns, sizes, product_sizes, item_id) => {
    const item = items[item_id];
    if (!item) {
      return [];
    }
    const isPoD = isPoDSource(item.copied_from);

    const optionItems = Object.values(items).filter(
      i => i.option_item_id === item_id
    ).sort(
      (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
    );

    const breakdownsByItemId = {};
    Object.values(breakdowns).forEach((breakdown) => {
      if (!breakdownsByItemId[breakdown.item_id]) {
        breakdownsByItemId[breakdown.item_id] = [];
      }
      breakdownsByItemId[breakdown.item_id].push(breakdown);
    });

    const mappedOptions = optionItems.map(i => {
      // each option item renders as a single pricing column, if legacy data has
      // extra breakdown rows for that item, use the first by display_order.
      const breakdown = (breakdownsByItemId[i.item_id] || []).slice().sort(
        (a, b) => parseInt(a.display_order ?? 0, 10) - parseInt(b.display_order ?? 0, 10)
      )[0] || {};
      const size = sizes[breakdown.size_id];
      const sizeName = (breakdown.size && breakdown.size !== 'TBD')
        ? breakdown.size
        : size?.size_name;

      const normalizedSizeName = isPoD
        ? normalizePodSizeLabel(sizeName)
        : sizeName;

      return {
        item_id: i.item_id,
        breakdown_id: breakdown.breakdown_id || '',
        hidden: i.hidden == 1,
        quantity: parseInt(breakdown.quantity || 0, 10),
        unit_cost: formatMoney(breakdown.unit_cost || 0, 4),
        total_margin: formatMoney(i.total_margin, 2),
        unit_price: formatMoney(i.unit_price, 2),
        costs_changed: i.costs_changed,
        exchange_rate: i.exchange_rate,
        size_id: breakdown.size_id,
        size_name: normalizedSizeName
      };
    });

    if (!isPoD) {
      return mappedOptions;
    }
    const availableSizeNames = getPodAvailableSizeNames(item, sizes, product_sizes);

    if (availableSizeNames.size === 0) {
      return toOneSizeOptions(mappedOptions);
    }

    const hasBaseSize = POD_BASE_SIZE_NAMES.some(sizeName => availableSizeNames.has(sizeName));
    const surchargeSizeNames = new Set(POD_SURCHARGE_SIZE_NAMES);
    const hasSurchargeSize = POD_SURCHARGE_SIZE_NAMES.some(sizeName => availableSizeNames.has(sizeName));
    const hasRecognizedApparelSize = hasBaseSize || hasSurchargeSize;

    if (!hasRecognizedApparelSize) {
      return toOneSizeOptions(mappedOptions);
    }

    // when XS-XL base exists, surcharge sizes (2XL–6XL) are edited via Up Charges
    const filteredOptions = mappedOptions.filter((option) => {
      const sizeLabel = normalizePodSizeName(option.size_name);
      if (!sizeLabel) {
        return false;
      }

      if (sizeLabel === normalizedOneSizeLabel) {
        return hasBaseSize;
      }

      if (sizeLabel === POD_BASE_SIZE_GROUP_LABEL) {
        return hasBaseSize;
      }

      if (surchargeSizeNames.has(sizeLabel)) {
        // surcharge-only products (no XS–XL) still need a pricing column
        return !hasBaseSize && availableSizeNames.has(sizeLabel);
      }

      return Number(option.quantity || 0) > 0;
    });

    return filteredOptions.map((option) => {
      const sizeLabel = normalizePodSizeName(option.size_name);
      if (sizeLabel !== normalizedOneSizeLabel) {
        return option;
      }

      return { ...option, size_name: POD_BASE_SIZE_GROUP_LABEL };
    });
  }
);

export const getProduct = createSelector(
  getProducts,
  getProductId,
  (products, product_id) => products[product_id]
);

export const getDivision = createSelector(
  getDivisions,
  getDivisionId,
  (divisions, division_id) => divisions[division_id]
);

export const getItemWarnings = createSelector(
  getWarnings,
  getItemId,
  (warnings, item_id) => Object.values(warnings).filter(w => w.item_id === item_id)
);

export const getItemRunCosts = createSelector(
  getItems,
  getCosts,
  getLocations,
  getItemId,
  (items, costs, locations, item_id) => Object.values(costs).filter(
    c => c.item_id === item_id && c.quantity === null
  ).map(
    c => ({
      item_cost_id: c.item_cost_id,
      item_cost_title: c.item_cost_title,
      isPS: !!c.ext_cost_id,
      ext_cost_id: c.ext_cost_id,
      item_location_id: c.item_location_id,
      options: Object.values(costs).filter(
        oc => oc.option_item_cost_id === c.item_cost_id && items[oc.item_id].parent_type === 'PRODUCT'
      ).map(
        oc => ({
          item_cost_id: oc.item_cost_id,
          item_id: oc.item_id,
          total_units: items[oc.item_id].total_units,
          hidden: items[oc.item_id].hidden == 1,
          unit_cost: formatMoney(oc.unit_cost, 4),
          isPS: !!oc.ext_cost_id,
          ext_cost_id: oc.ext_cost_id
        })
      ).sort(
        (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
      ),
      location_title: (locations[c.item_location_id] || {}).item_location_title,
      location_index: (Object.values(locations).filter(
        l => l.item_id === item_id
      ).sort(
        (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
      ).map(
        (l, index) => ({
          ...l,
          index: index + 1
        })
      ).filter(
        l => l.item_location_id === c.item_location_id
      )[0] || {}).index
    })
  ).sort(
    (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
  )
);

export const getItemFixedCosts = createSelector(
  getCosts,
  getLocations,
  getItemId,
  (costs, locations, item_id) => Object.values(costs).filter(
    c => c.item_id === item_id && c.quantity !== null
  ).map(
    c => ({
      item_cost_id: c.item_cost_id,
    })
  ).sort(
    (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
  )
);

export const getItemAdjustments = createSelector(
  getAdjustments,
  getItems,
  getProducts,
  getItemId,
  (adjustments, items, products, item_id) => Object.values(adjustments).filter(
    a => a.item_id === item_id
  ).map(
    a => ({
      ...a,
      product_id: items[a.item_id].parent_id,
      value: !!a.axis ? { axis: a.axis, option: a.option } : { sku: a.sku }
    })
  ).sort(
    (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
  )
);

export const getItemCost = createSelector(
  getCosts,
  getItems,
  getLocations,
  getItemCostId,
  (costs, items, locations, item_cost_id) => {
    const cost = costs[item_cost_id];
    return {
      item_cost_id,
      item_cost_title: cost.item_cost_title,
      unit_cost: formatMoney(cost.unit_cost),
      total_margin: formatMoney(cost.total_margin),
      unit_price: formatMoney(cost.unit_price),
      total_units: cost.quantity || items[cost.item_id].total_units,
      location_title: (locations[cost.item_location_id] || {}).item_location_title,
      location_index: (Object.values(locations).filter(
        l => l.item_id === cost.item_id
      ).sort(
        (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
      ).map(
        (l, index) => ({
          ...l,
          index: index + 1
        })
      ).filter(
        l => l.item_location_id === cost.item_location_id
      )[0] || {}).index,
      item_location_id: cost.item_location_id,
      ext_cost_id: cost.ext_cost_id
    };
  }
);

export const getItemComments = createSelector(
  getComments,
  getUsers,
  getItemId,
  (comments, users, item_id) => Object.values(comments).filter(
    c => c.presentation_item_id === item_id
  ).map(
    c => ({
      ...c,
      image: Object.values(users || {}).filter(u => u.login_name === c.commenter_email).map(u => getUserImageSrc(u))[0]
    })
  ).sort(
    (a, b) => Date.parse(b.date_created) - Date.parse(a.date_created)
  )
);

export const getItemDecoration = createSelector(
  getDecorations,
  getLocations,
  getDecorationId,
  (decorations, locations, item_decoration_id) => {
    const decoration = decorations[item_decoration_id];
    if (!decoration) {
      return null;
    }
    return {
      ...decoration,
      ext_location_id: locations[decoration.item_location_id].ext_location_id
    };
  }
);

export const getItemLocations = createSelector(
  getLocations,
  getDecorations,
  getArtworks,
  getFiles,
  getItemId,
  (locations, decorations, artworks, files, item_id) => Object.values(locations).filter(
    l => l.item_id === item_id
  ).map(
    l => ({
      ...l,
      artworks: Object.values(artworks).filter(
        a => a.item_location_id === l.item_location_id
      ).map(
        a => ({
          ...a,
          image: getArtworkDisplayImage(a, files),
        })
      ),
      decorations: Object.values(decorations).filter(
        d => d.item_location_id === l.item_location_id
      ),
      location_index: (Object.values(locations).filter(
        l2 => l2.item_id === item_id
      ).sort(
        (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
      ).map(
        (l2, index) => ({
          ...l2,
          index: index + 1
        })
      ).filter(
        l2 => l2.item_location_id === l.item_location_id
      )[0] || {}).index
    })
  ).sort(
    (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
  )
);

export const getBasicItemLocations = createSelector(
  getLocations,
  getItemId,
  (locations, item_id) => Object.values(locations).filter(l => l.item_id === item_id)
);

export const getLocation = createSelector(
  getLocations,
  getLocationId,
  (locations, item_location_id) => locations[item_location_id]
);

export const getArtwork = createSelector(
  getArtworks,
  getFiles,
  getArtworkId,
  (artworks, files, artwork_id) => ({
    ...artworks[artwork_id],
    image: files[artworks[artwork_id].artwork_file_id] || null,
  })
);

export const getOrder = createSelector(
  getItems,
  getOrders,
  getItemId,
  (items, orders, item_id) => orders[items[item_id].order_id]
);

export const getOrderByNumber = createSelector(
  getOrders,
  getOrderType,
  getOrderNumber,
  (orders, order_type, form_number) => Object.values(orders).filter(
    o => +o.form_number === form_number && o.order_type === order_type
  )[0]
);

export const getOrderByItemLocationId = createSelector(
  getOrders,
  getItems,
  getLocations,
  getLocationId,
  (orders, items, locations, item_location_id) => orders[items[locations[item_location_id].item_id].order_id]
);

export const getItemImages = createSelector(
  getImages,
  getFiles,
  getItemId,
  (images, files, item_id) => Object.values(images).filter(
    ii => ii.item_id === item_id
  ).map(
    ii => ({
      ...ii,
      image: files[ii.file_id]
    })
  ).sort(
    (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
  )
);
